import React from "react";

const TEXT = "\uFE0E";
const PLANET_SYMBOLS = Object.fromEntries(Object.entries({
  sun: "☉", moon: "☽", mercury: "☿", venus: "♀", mars: "♂", jupiter: "♃",
  saturn: "♄", uranus: "♅", neptune: "♆", pluto: "♇"
}).map(([slug, symbol]) => [slug, symbol + TEXT]));

// The numbers, in a table, next to the wheel. A chart that cannot be checked
// against another tool is not much use, so the intermediate values — Julian
// day, sidereal time, obliquity, the offset actually applied — are on the page
// rather than hidden behind the drawing.
function Row({ label, value, mono }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="readout__row">
      <dt>{label}</dt>
      <dd className={mono ? "readout__mono" : undefined}>{value}</dd>
    </div>
  );
}

export default function ChartReadout({ chart }) {
  const { moment, angles, houses, aspects, planets, ephemeris } = chart;

  return (
    <div className="readout" data-testid="chart-readout">
      <section className="readout__block readout__block--planets">
        <h2>Planets <a className="readout__reference" href="/astrology/planets">guide</a></h2>
        {ephemeris.available ? (
          <>
            <ol className="readout__planets">
              {planets.map((planet) => (
                <li key={planet.slug} data-testid={`readout-planet-${planet.slug}`}>
                  <a className="readout__planet-name" href={`/astrology/planets/${planet.slug}`}>
                    <span className="readout__planet-symbol" aria-hidden="true">{PLANET_SYMBOLS[planet.slug]}</span>{" "}
                    {planet.slug.replace(/_/g, " ")}
                  </a>
                  <span>{planet.formatted}</span>
                  <span className="readout__house">house {planet.house}</span>
                  {planet.retrograde && <span className="readout__retro">℞</span>}
                </li>
              ))}
            </ol>
            <p className="readout__note" data-testid="ephemeris-provenance">
              Calculated with {ephemeris.name} {ephemeris.version}, {ephemeris.mode}.{" "}
              <a href={ephemeris.sourceUrl} target="_blank" rel="noopener noreferrer">Engine documentation</a>.
              License: {ephemeris.license}.
            </p>
          </>
        ) : <p className="readout__warning" data-testid="no-ephemeris">{ephemeris.reason}</p>}
      </section>

      <section className="readout__block readout__block--houses">
        <h2>Houses <span className="readout__qualifier">{houses.systemName}</span>{" "}
          <a className="readout__reference" href="/astrology/houses">guide</a>
        </h2>
        <ol className="readout__cusps">
          {houses.cusps.map((cusp) => (
            <li key={cusp.house} data-testid={`readout-cusp-${cusp.house}`}>
              <a className="readout__cusp-number" href={`/astrology/houses/${cusp.house}`}>{cusp.house}</a>
              <span>{cusp.formatted}</span>
            </li>
          ))}
        </ol>
      </section>

      <section className="readout__block">
        <h2>The moment</h2>
        <dl>
          <Row label="Local reading" value={moment.reading} />
          <Row label="Zone" value={`${moment.timezone} (${moment.zoneAbbreviation})`} />
          <Row
            label="Offset applied"
            value={`UTC${moment.utcOffsetHours >= 0 ? "+" : ""}${moment.utcOffsetHours}${
              moment.dst ? ", summer time" : ""
            }`}
          />
          <Row label="UTC" value={moment.utc} mono />
          <Row label="Julian day (UT)" value={moment.julianDay} mono />
          <Row label="Latitude" value={moment.latitude} mono />
          <Row label="Longitude" value={moment.longitude} mono />
        </dl>
        {moment.wasAmbiguous && (
          <p className="readout__note" data-testid="ambiguity-resolved">
            That local time occurred twice. This chart uses the{" "}
            <strong>{moment.occurrence === "first" ? "first" : "second"}</strong> of them.
          </p>
        )}
      </section>

      <section className="readout__block">
        <h2>Angles</h2>
        <dl>
          <Row label="Ascendant" value={angles.ascendant.formatted} />
          <Row label="Midheaven" value={angles.midheaven.formatted} />
          <Row label="Descendant" value={angles.descendant.formatted} />
          <Row label="Imum Coeli" value={angles.imumCoeli.formatted} />
          <Row label="Local sidereal time" value={`${angles.localSiderealTime}°`} mono />
          <Row label="Obliquity" value={`${angles.obliquity}°`} mono />
        </dl>
        {angles.caveat && (
          <p className="readout__warning" data-testid="polar-caveat">{angles.caveat}</p>
        )}
      </section>

      <section className="readout__block">
        <h2>Aspects <span className="readout__qualifier">{aspects.orbSetName} orbs</span></h2>
        <p className="readout__note">{aspects.orbSetNote}</p>
        {aspects.list.length === 0 ? (
          <p className="readout__note">No aspects fall within the selected orbs.</p>
        ) : (
          <ol className="readout__aspects">
            {aspects.list.map((aspect) => (
              <li key={`${aspect.from}-${aspect.to}-${aspect.aspect}`}
                  data-testid={`readout-aspect-${aspect.from}-${aspect.to}`}>
                <span className="readout__aspect-pair">
                  {aspect.from.replace(/_/g, " ")} <span className="readout__aspect-symbol">{aspect.symbol}</span>{" "}
                  {aspect.to.replace(/_/g, " ")}
                </span>
                <span className="readout__orb">orb {aspect.orb.toFixed(2)}°</span>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
